/**
 * Thread @-mention resolver — pure (group-threading P2b).
 *
 * Turns a sent message's text into the set of workspace-member uids to summon.
 * Resolves two forms against the account roster:
 *   1. `<@uid>` tokens (precise — inserted by the autocomplete).
 *   2. plain `@query` mentions (typed) — matched UNAMBIGUOUSLY against the roster in
 *      two tiers: an EXACT identifier (uid / email / email-local-part) first, and a
 *      whitespace-stripped displayName PREFIX only when nothing matched exactly.
 *
 * FAIL-CLOSED: a `@query` that matches zero OR more than one member is skipped (never
 * summon the wrong person). The server (`threadMembershipWrite` via P2a) stays the
 * sole authority — this only proposes uids. Pure + React-free.
 */
import { parseMentionUids } from './mentionParser';

export interface MentionRosterEntry {
  userId: string;
  displayName?: string;
  email?: string;
  /**
   * The profile's contact e-mail, when the member doc's own `email` is blank
   * or differs (2026-09-08). An exact match on EITHER address, or its
   * local-part, names the person precisely.
   */
  profileEmail?: string;
}

/** `@query` where `@` opens a word; query is name/email-ish chars (no spaces, no `@`). */
/**
 * The ONE grammar of a plain mention, exported as a SOURCE string (2026-09-21).
 *
 * A consumer that needs to find or replace mentions rebuilds its own `RegExp`
 * from this rather than inventing a second pattern or sharing this object: a
 * global regex carries `lastIndex`, so a shared instance turns two callers into
 * one intermittent bug. Sharing the source is what keeps `@` in an e-mail
 * address from ever reading as a mention on one surface and not another.
 */
export const PLAIN_MENTION_SOURCE = '(?:^|\\s)@([A-Za-z0-9._%+-]+)';

const PLAIN_MENTION = new RegExp(PLAIN_MENTION_SOURCE, 'g');

/**
 * "Everyone in this thread" (Miles, 2026-09-18) — and on THIS side it resolves
 * to NOBODY, which is not a gap but the point.
 *
 * The twin of this function on the server is handed the THREAD's participants,
 * so `@team` there means the room and is expanded to it
 * (functions/src/prest0nSession/mentionResolution.ts). This one is handed the
 * whole ACCOUNT roster, and its single caller — `useThreadMentionSummon` — uses
 * the result to ADD people to the thread. Expanding a group token here would
 * offer to add the entire firm to a conversation because somebody typed one
 * word, which is the 2026-09-15 consent ruling inverted and automated.
 *
 * `@team` addresses people who are ALREADY in the thread, so there is by
 * definition nobody for this side to add. Both halves are therefore correct and
 * they do not disagree about who is notified: the server decides that, alone.
 *
 * It is named here, rather than simply falling through the tiers, so that
 * `findUnresolvedMentionHandles` can tell "addressed the room" from "matched
 * nobody" — otherwise typing @team would raise "no teammate matches @team"
 * under a message that in fact reached everyone.
 */
const TEAM_TOKENS: ReadonlySet<string> = new Set(['team', 'everyone']);

/** The spelling the composer inserts; the dropdown's synthetic candidate uses it. */
export const TEAM_MENTION_HANDLE = 'team';

/** True when this `@query` addresses the whole thread rather than a person. */
export function isTeamMentionQuery(query: string): boolean {
  return TEAM_TOKENS.has(norm(query));
}

/** True when the text carries a team mention anywhere. */
export function textMentionsTeam(text: string): boolean {
  return extractPlainMentionQueries(text).some((q) => isTeamMentionQuery(q));
}

function norm(v: string | undefined): string {
  return (v ?? '').trim().toLowerCase();
}

/**
 * Whitespace-stripped, lowercased name — so "QA Teammate" matches a "@qa" query.
 *
 * Punctuation the `@query` grammar cannot carry is also stripped (2026-09-08).
 * A plain mention is captured by PLAIN_MENTION, which accepts only
 * `[A-Za-z0-9._%+-]`, and `deriveMentionHandle` sanitizes its offered handles to
 * that same set — so it offered "obrien" for "O'Brien" while this comparison
 * still demanded the apostrophe, and the handle could never verify. Anyone with
 * an apostrophe or an accent in their name (O'Brien, D'Angelo, Muñoz) was
 * therefore unmentionable by any readable handle and fell through to a raw uid.
 * Sanitizing BOTH sides is what makes the two halves agree.
 */
const HANDLE_UNSAFE = /[^A-Za-z0-9._%+-]/g;
function nameKey(v: string | undefined): string {
  return norm(v).replace(/\s+/g, '').replace(HANDLE_UNSAFE, '');
}

function emailLocal(email: string | undefined): string {
  const e = norm(email);
  const at = e.indexOf('@');
  return at > 0 ? e.slice(0, at) : e;
}

/** Extract plain `@query` tokens (word-opening `@`), excluding `<@uid>` tokens. */
export function extractPlainMentionQueries(text: string): string[] {
  const out: string[] = [];
  if (!text) return out;
  PLAIN_MENTION.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = PLAIN_MENTION.exec(text)) !== null) {
    const q = (m[1] ?? '').trim();
    if (q) out.push(q);
  }
  return out;
}

/**
 * The two tiers, for ONE query: exact on an identifier the person owns, else a
 * display-name prefix. Returns EVERY match — the caller decides (exactly one
 * resolves; zero or several resolve to nobody), so fail-closed stays the
 * caller's rule and cannot be softened by adding a retry above it.
 */
function matchQuery(
  members: ReadonlyArray<MentionRosterEntry>,
  q: string,
): ReadonlyArray<MentionRosterEntry> {
  const exact = members.filter(
    (m) => norm(m.userId) === q
      || norm(m.email) === q || emailLocal(m.email) === q
      || (!!norm(m.profileEmail) && (norm(m.profileEmail) === q || emailLocal(m.profileEmail) === q)),
  );
  if (exact.length > 0) return exact;
  return members.filter(
    (m) => nameKey(m.displayName).length > 0 && nameKey(m.displayName).startsWith(q),
  );
}

/**
 * A query with its sentence-ending dots taken off — the ONE retry the resolver
 * allows, and only for a query that reached nobody.
 *
 * "…and ask @Miles." is how a person and a model both write a name at the end
 * of a sentence, and `PLAIN_MENTION`'s character class swallows the full stop,
 * so the query is `miles.` and every tier misses. Measured live on 2026-09-21
 * against the real prest0-inc roster: `@Miles.` resolved to NOBODY while
 * `@Miles`, `@miles` and `@MilesMaggio` all resolved. A comma, `!` or `?` never
 * had this problem: they are outside the class and were never captured.
 *
 * Only a TRAILING run of dots, and only after the normal path found nobody. An
 * interior dot is load-bearing (a `first.last@` local part matches today), and
 * a query that already resolved — or that resolved ambiguously — is left
 * exactly as it was: the retry can turn a silence into a person, never one
 * person into another.
 *
 * TWIN of the server's rule (`functions/src/prest0nSession/mentionResolution.ts`),
 * pinned by behaviour table in tests/prest0nSession/mentionResolution.parity.test.ts.
 */
function withoutTrailingDots(q: string): string {
  return q.replace(/\.+$/, '');
}

/**
 * Resolve a message's mentions to a de-duplicated list of roster uids to summon.
 * `<@uid>` tokens are kept only if they name a real roster member; plain `@query`
 * mentions resolve only when EXACTLY one member matches.
 */
export function resolveMentionsToUids(
  text: string,
  roster: ReadonlyArray<MentionRosterEntry>,
): string[] {
  const members = (roster ?? []).filter((m) => m && String(m.userId ?? '').trim());
  const byId = new Map<string, string>(); // uid → uid (identity, for membership check)
  for (const m of members) byId.set(String(m.userId).trim(), String(m.userId).trim());

  const result: string[] = [];
  const seen = new Set<string>();
  const add = (uid: string) => {
    const id = (uid ?? '').trim();
    if (id && byId.has(id) && !seen.has(id)) {
      seen.add(id);
      result.push(id);
    }
  };

  // 1. Precise <@uid> tokens (validated against the roster).
  for (const uid of parseMentionUids(text)) add(uid);

  // 2. Plain @query mentions — unambiguous match only, in TWO TIERS.
  //
  // TIERED PRECEDENCE (2026-09-08, Fernando-workspace report): an EXACT match on
  // an identifier the person owns (uid / email / email local-part) is a precise
  // reference to THEM. A displayName PREFIX match is a guess at who was meant.
  // Weighing both equally let one person's NAME make another person's OWN email
  // ambiguous: a member doc carrying a COLLEAGUE's stale name meant a query that
  // was one teammate's exact email local-part also prefix-matched the other's
  // name — two matches, fail-closed, no summon. And because deriveMentionHandle
  // only offers handles this resolver verifies, the composer fell through to the
  // raw uid and put a 28-character id in a client-visible thread.
  //
  // Exact first, prefix only when nothing matched exactly. Two real teammates
  // who genuinely share a name can no longer break each other's mentions.
  // Fail-closed is UNCHANGED and applies WITHIN each tier: a tier that matches
  // zero or several people yields no summon, and an ambiguous exact tier never
  // falls through to a prefix guess (an exact hit means the query named an
  // identifier — the answer is there or nowhere).
  for (const rawQuery of extractPlainMentionQueries(text)) {
    const q = norm(rawQuery);
    if (!q) continue;
    // The room, not a person — and never anybody to ADD (see TEAM_TOKENS).
    // Skipped explicitly rather than left to fail-closed, because a teammate
    // whose display name begins "team…" would otherwise be summoned into the
    // thread by a group mention that was never about them.
    if (TEAM_TOKENS.has(q)) continue;
    let matches = matchQuery(members, q);
    if (matches.length === 0) {
      // Nobody at all — retry once without the sentence's full stop.
      const undotted = withoutTrailingDots(q);
      if (undotted && undotted !== q) matches = matchQuery(members, undotted);
    }
    if (matches.length === 1) add(String(matches[0].userId).trim());
    // 0 or >1 matches in the deciding tier ⇒ ambiguous ⇒ skip (fail-closed).
  }

  return result;
}
